/**
 * editor Utilities
 * Helper functions used throughout the editor
 */

// TextDirection type is now defined inline since editor.types.ts was removed
type TextDirection = 'ltr' | 'rtl'

/**
 * Detects text direction based on the first character (legacy single-char detection)
 * @param char - The character to analyze
 * @returns 'rtl' for right-to-left scripts, 'ltr' for left-to-right, null for neutral
 */
export const detectTextDirection = (char: string): TextDirection | null => {
  // RTL scripts: Hebrew, Arabic, and related (simplified single range)
  if (/[\u0590-\u08FF]/.test(char)) {
    return 'rtl'
  }

  // LTR: Basic Latin letters
  if (/[A-Za-z]/.test(char)) {
    return 'ltr'
  }

  // Everything else is neutral
  return null
}

/**
 * Enhanced text direction detection - finds first strong directional character
 * @param text - The text to analyze
 * @returns 'rtl' for right-to-left scripts, 'ltr' for left-to-right, null for neutral/no strong chars
 */
export const detectParagraphDirection = (text: string): TextDirection | null => {
  if (!text || typeof text !== 'string') {
    return null
  }

  // Find first strong directional character
  for (const char of text) {
    // RTL strong characters - Hebrew, Arabic, Persian, Urdu, etc.
    if (/[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F\u0780-\u07BF\u08A0-\u08FF\uFB1D-\uFB4F\uFE70-\uFEFF]/.test(char)) {
      return 'rtl'
    }

    // LTR strong characters - Latin, Cyrillic, Greek, etc.
    if (/[A-Za-z\u00C0-\u017F\u0100-\u024F\u0370-\u03FF\u0400-\u04FF\u1E00-\u1EFF]/.test(char)) {
      return 'ltr'
    }

    // Skip neutral characters (numbers, punctuation, spaces, symbols)
    // These don't affect paragraph direction
  }

  return null // No strong directional characters found
}


